import { ParsedInputResult, parseInput } from '@jfinchain/jnsjs/utils/validation'
import { useQuery } from 'wagmi'

import { Prettify } from '@app/types'
import { tryBeautify } from '@app/utils/beautify'
import { useQueryKeys } from '@app/utils/cacheKeyFactory'
import { isBlacklisted, isEnglishLowerCaseOrNumeric } from '@app/utils/wording'

export type ValidationResult = Prettify<
  Partial<Omit<ParsedInputResult, 'normalised' | 'labelDataArray'>> & {
    name: string
    beautifiedName: string
    isNonASCII: boolean | undefined
    labelCount: number
    labelDataArray: ParsedInputResult['labelDataArray']
  }
>

const tryDecodeURIComponent = (input: string) => {
  try {
    return decodeURIComponent(input)
  } catch {
    return input
  }
}

export const validate = (input: string) => {
  const decodedInput = tryDecodeURIComponent(input)
  const { normalised: name, ...parsedInput } = parseInput(decodedInput)
  const isNonASCII = parsedInput.labelDataArray.some((dataItem) => dataItem.type !== 'ASCII')
  const outputName = name || input
  const isValid = isEnglishLowerCaseOrNumeric(outputName) && !isBlacklisted(outputName || '')

  return {
    ...parsedInput,
    isValid,
    name: outputName,
    beautifiedName: tryBeautify(outputName),
    isNonASCII,
    labelCount: parsedInput.labelDataArray.length,
  }
}

const defaultData = Object.freeze({
  name: '',
  beautifiedName: '',
  isNonASCII: undefined,
  labelCount: 0,
  type: undefined,
  isValid: undefined,
  isShort: undefined,
  is2LD: undefined,
  isETH: undefined,
  labelDataArray: [],
})

export const useValidate = (input: string, skip?: any): ValidationResult => {
  const { data } = useQuery(useQueryKeys().validate(input), () => validate(input), {
    enabled: !skip,
    initialData: () => (skip ? defaultData : validate(input)),
  })

  return data
}
